import { useCallback, useRef, useState } from "react";
import { Box, Text, VStack, useToast } from "@chakra-ui/react";
import type {
  CampaignIntent,
  DisplayMarketingCampaign,
} from "@common/interfaces/KYDMarketing";
import {
  buildIntentApplyImmediateToastCopy,
  isActionableIntent,
  type ActionableCampaignIntent,
} from "common/interfaces/CampaignIntent";
import type {
  FacebookPage,
  InstagramAccount,
} from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/types";
import actions from "src/actions";
import AdSetCards from "./AdSetCards";
import PerformanceInsights from "./PerformanceInsights";
import {
  getBatchApplyHardFailureMessage,
  getBatchApplyPartialWarningMessage,
  type BatchIntentApplyApiResponse,
} from "src/Manager/Campaigns/Ads/batchIntentApplyTypes";
import {
  collectBatchIntentApplyUpdateTargets,
  pollBatchIntentApplyUpdates,
} from "src/Manager/Campaigns/Ads/utils/pollBatchIntentApplyUpdates";

const GRID_BG =
  "linear-gradient(rgba(0,0,0,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.08) 1px, transparent 1px)";

export interface RightPanelProps {
  campaign: DisplayMarketingCampaign;
  pages: FacebookPage[];
  instagramAccounts: InstagramAccount[];
  onEditCampaign: () => void;
  onEditAdset: (adsetId: string) => void;
  onEditAd?: (
    ad: import("@common/interfaces/KYDMarketing").DisplayMarketingAd,
    adset?: import("@common/interfaces/KYDMarketing").DisplayMarketingAdSet
  ) => void;
  onRefetch: () => void;
  onRefetchSilent: () => void | Promise<void>;
  onPauseCampaign: () => void;
  onPublishCampaign: (p: { id: string }) => void;
  onUnpublishCampaign: (p: { id: string }) => void;
  onPublishAdset: (p: { campaign_id: string; adset_id: string }) => void;
  onPublishAd?: (p: {
    campaign_id: string;
    adset_id: string;
    ad_id: string;
  }) => void | Promise<void>;
  onUnpublishAdset: (p: { campaign_id: string; adset_id: string }) => void;
  onUnpublishAd: (p: {
    campaign_id: string;
    adset_id: string;
    ad_id: string;
  }) => void | Promise<void>;
  isPublishingCampaign?: boolean;
  activatingAdKey?: string | null;
  pausingAdsetId?: string | null;
  publishingAdsetId?: string | null;
  pausingAdKey?: string | null;
  onOpenFocusedCreativeEdit?: (adsetId: string, creativeIndex?: number) => void;
  isApplyingChanges?: boolean;
  /** Same publish polling UX as guided edits (header status + managed loading). */
  pollPublishAfterIntentApply?: (p: {
    campaign_id: string;
    update_id: string;
    intent: ActionableCampaignIntent;
  }) => Promise<void>;
}

const getAllIntentsInCampaign = (campaign: DisplayMarketingCampaign) => {
  const campaignIntents = campaign.intents ?? [];
  const adsets = campaign.adsets ?? [];
  const adsetIntents = adsets.flatMap((adset) => adset.intents ?? []);
  const adIntents = adsets.flatMap((adset) =>
    (adset.ads ?? []).flatMap((ad) => ad.intents ?? [])
  );
  return [...campaignIntents, ...adsetIntents, ...adIntents];
};

export default function RightPanel({
  campaign,
  pages,
  instagramAccounts,
  onEditCampaign,
  onEditAdset,
  onEditAd,
  onRefetch,
  onRefetchSilent,
  onPauseCampaign,
  onPublishCampaign,
  onUnpublishCampaign,
  onPublishAdset,
  onPublishAd,
  onUnpublishAdset,
  onUnpublishAd,
  isPublishingCampaign,
  activatingAdKey,
  pausingAdsetId,
  publishingAdsetId,
  pausingAdKey,
  onOpenFocusedCreativeEdit,
  isApplyingChanges = false,
  pollPublishAfterIntentApply,
}: RightPanelProps) {
  const adsetCardRefs = useRef<Record<string, HTMLElement | null>>({});
  const setAdsetCardRef = useCallback(
    (adsetId: string, el: HTMLElement | null) => {
      if (el) adsetCardRefs.current[adsetId] = el;
      else delete adsetCardRefs.current[adsetId];
    },
    []
  );
  const scrollToAdset = useCallback((adsetId: string) => {
    adsetCardRefs.current[adsetId]?.scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "center",
    });
  }, []);

  const toast = useToast();
  const [applyingIntentId, setApplyingIntentId] = useState<string | null>(null);
  const [ignoringIntentId, setIgnoringIntentId] = useState<string | null>(null);
  const handleApplyIntent = useCallback(
    async (intent: CampaignIntent) => {
      if (!isActionableIntent(intent)) return;
      const s = intent.status as string | undefined;
      if (s !== "SUGGESTED" && s !== undefined) return;
      setApplyingIntentId(intent.id);
      try {
        const operations = [
          { campaign_id: campaign.id, intent_ids: [intent.id] },
        ];
        const response: BatchIntentApplyApiResponse =
          await actions.ADMIN.batchApplyCampaignIntents(operations);
        const hardFail = getBatchApplyHardFailureMessage(response);
        if (hardFail) throw new Error(hardFail);
        const targets = collectBatchIntentApplyUpdateTargets(
          operations,
          response
        );
        if (
          pollPublishAfterIntentApply &&
          targets.length === 1 &&
          targets[0].campaign_id === campaign.id
        ) {
          await pollPublishAfterIntentApply({
            campaign_id: targets[0].campaign_id,
            update_id: targets[0].update_id,
            intent,
          });
          const partial = getBatchApplyPartialWarningMessage(response);
          const copy = buildIntentApplyImmediateToastCopy(intent, partial);
          toast({
            title: copy.title,
            description: copy.description,
            status: partial ? "warning" : "success",
            isClosable: true,
          });
          await onRefetchSilent();
        } else {
          const polling = await pollBatchIntentApplyUpdates({
            operations,
            response,
          });
          if (polling.failed) {
            throw new Error(
              polling.errorMessage ??
                "Could not verify campaign publish update."
            );
          }
          const partial = getBatchApplyPartialWarningMessage(response);
          const copy = buildIntentApplyImmediateToastCopy(intent, partial);
          toast({
            title: copy.title,
            description: copy.description,
            status: partial ? "warning" : "success",
            isClosable: true,
          });
          await onRefetchSilent();
        }
      } catch (err: unknown) {
        toast({
          title: "Failed to apply",
          description:
            (err as Error)?.message ??
            "Could not apply the recommended action.",
          status: "error",
          isClosable: true,
        });
      } finally {
        setApplyingIntentId(null);
      }
    },
    [campaign.id, onRefetchSilent, pollPublishAfterIntentApply, toast]
  );
  const handleIgnoreIntent = useCallback(
    async (intent: CampaignIntent) => {
      setIgnoringIntentId(intent.id);
      try {
        await actions.ADMIN.ignoreCampaignIntent(campaign.id, [intent]);
        const label =
          intent.explanation?.short_summary ??
          intent.explanation?.summary ??
          intent.actions?.[0]?.label ??
          intent.reason ??
          "Insight";
        toast({
          title: "Insight dismissed",
          description: label.length > 80 ? `${label.slice(0, 77)}…` : label,
          status: "success",
          isClosable: true,
        });
        await onRefetchSilent();
      } catch (err: unknown) {
        toast({
          title: "Could not ignore",
          description:
            (err as Error)?.message ?? "Intent could not be saved as ignored.",
          status: "warning",
          isClosable: true,
        });
      } finally {
        setIgnoringIntentId(null);
      }
    },
    [campaign.id, onRefetchSilent, toast]
  );

  return (
    <Box
      flex="1"
      minW={0}
      overflowY="auto"
      display="flex"
      flexDirection="column"
      position="relative"
      bg="#C9C9C9"
      backgroundImage={GRID_BG}
      backgroundSize="48px 48px"
      backgroundPosition="8px 24px"
    >
      <VStack align="stretch" spacing={5} p={4} flex="1">
        <PerformanceInsights
          campaignId={campaign.id}
          intents={getAllIntentsInCampaign(campaign)}
          campaign={campaign}
          onApplied={onRefetchSilent}
          onScrollToAdset={scrollToAdset}
          pollPublishAfterIntentApply={pollPublishAfterIntentApply}
        />
        <AdSetCards
          campaign={campaign}
          pages={pages}
          instagramAccounts={instagramAccounts}
          onEditCampaign={onEditCampaign}
          onEditAdset={onEditAdset}
          onEditAd={onEditAd}
          onPauseCampaign={onPauseCampaign}
          onPublishCampaign={onPublishCampaign}
          onUnpublishCampaign={onUnpublishCampaign}
          onPublishAdset={onPublishAdset}
          onPublishAd={onPublishAd}
          onUnpublishAdset={onUnpublishAdset}
          onUnpublishAd={onUnpublishAd}
          isPublishingCampaign={isPublishingCampaign}
          activatingAdKey={activatingAdKey}
          pausingAdsetId={pausingAdsetId}
          publishingAdsetId={publishingAdsetId}
          pausingAdKey={pausingAdKey}
          onRefetch={onRefetch}
          onApplyIntent={handleApplyIntent}
          onIgnoreIntent={handleIgnoreIntent}
          applyingIntentId={applyingIntentId}
          ignoringIntentId={ignoringIntentId}
          onOpenWizardReview={onOpenFocusedCreativeEdit}
          registerCardRef={setAdsetCardRef}
          isApplyingChanges={isApplyingChanges}
        />
      </VStack>
    </Box>
  );
}
